/**
 * InsulinAxisCard — iter1055 — Една инсулинова ос с две страни.
 * Страна А: DIL от храната (натиск, 2-3 ч след хранене)
 * Страна Б: HOMA-IR от кръвта (отговор на гладно)
 * Страната на кръвта решава колко тежи страната на храната.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Loader2, Info, ArrowRight, Utensils, Droplet } from "lucide-react";

const bandColor = (s) =>
  s === null || s === undefined ? "#64748B" : s >= 80 ? "#10B981" : s >= 60 ? "#84CC16" : s >= 40 ? "#F59E0B" : "#EF4444";

const SideBlock = ({ side, icon: Icon, isDark, testid }) => (
  <div className={`p-3 rounded-xl border ${isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 opacity-70" />
      <span className="text-xs font-bold">{side.title_bg}</span>
      <span className="ml-auto text-lg font-bold" style={{ color: bandColor(side.score) }}>
        {(side.display_value ?? side.value) ?? "—"}
      </span>
    </div>
    <div className="text-[10px] uppercase tracking-wide opacity-50">{side.display_unit_bg || side.unit_bg} · {side.window_bg}</div>
    <div className="text-xs mt-1 font-semibold" style={{ color: bandColor(side.score) }}>{side.band_bg}</div>
    <div className="text-xs opacity-75 mt-1 leading-relaxed">{side.detail_bg}</div>
    {side.proxy_note_bg && <div className="text-[11px] opacity-70 mt-1 italic">{side.proxy_note_bg}</div>}
  </div>
);

export default function InsulinAxisCard({ isDark }) {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/insulin-axis`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API]);

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  if (loading) {
    return (
      <div className={`p-5 rounded-2xl border ${cardCls} flex items-center justify-center`} data-testid="insulin-axis-loading">
        <Loader2 className="w-5 h-5 animate-spin text-lime-500" />
      </div>
    );
  }
  if (!data) return null;

  const { sides } = data;

  return (
    <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="insulin-axis-card">
      <div className="flex items-baseline gap-3 mb-1">
        <div>
          <div className="text-[10px] uppercase font-bold opacity-60 tracking-wide">Инсулинова ос</div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold" style={{ color: bandColor(data.axis_score) }} data-testid="insulin-axis-score">
              {data.axis_score ?? "—"}
            </span>
            <span className="text-xs opacity-60">/100</span>
          </div>
        </div>
        <span className="ml-auto text-xs px-2 py-1 rounded-md font-bold text-white" style={{ background: bandColor(data.axis_score) }} data-testid="insulin-axis-label">
          {data.axis_label_bg}
        </span>
      </div>

      {data.quadrant && (
        <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-lime-900/20 border border-lime-500/30" : "bg-lime-50 border border-lime-200"}`} data-testid="insulin-axis-quadrant">
          <div className="text-sm font-bold" data-testid="insulin-axis-quadrant-title">{data.quadrant.title_bg}</div>
          <div className="text-xs opacity-80 mt-1 leading-relaxed">{data.quadrant.text_bg}</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <SideBlock side={sides.load} icon={Utensils} isDark={isDark} testid="insulin-axis-side-load" />
        <SideBlock side={sides.sensitivity} icon={Droplet} isDark={isDark} testid="insulin-axis-side-sensitivity" />
      </div>

      {data.amplification?.text_bg && (
        <div className="text-xs mt-3 leading-relaxed opacity-90" data-testid="insulin-axis-amplification">
          <span className="font-bold">Колко тежи храната сега: </span>{data.amplification.text_bg}
        </div>
      )}

      {data.lever && (
        <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`} data-testid="insulin-axis-lever">
          <div className="text-xs font-bold mb-1">{data.lever.title_bg}</div>
          <ul className="text-xs space-y-1 opacity-85">
            {data.lever.steps_bg.map((s, i) => <li key={i}>• {s}</li>)}
          </ul>
          <button
            onClick={() => navigate(data.lever.route)}
            className="mt-2 text-xs font-bold text-lime-600 hover:underline flex items-center gap-1"
            data-testid="insulin-axis-lever-btn"
          >
            {data.lever.route_label_bg}
          </button>
        </div>
      )}

      {data.missing_bg?.length > 0 && (
        <div className="text-xs mt-3 opacity-75" data-testid="insulin-axis-missing">
          <span className="font-bold">За пълна ос липсва: </span>{data.missing_bg.join("; ")}.
        </div>
      )}

      {data.links?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="insulin-axis-links">
          {data.links.map((l, i) => (
            <button key={i} onClick={() => navigate(l.route)}
              className={`text-[11px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700 hover:bg-slate-800" : "border-slate-200 hover:bg-slate-100"}`}
              data-testid={`insulin-axis-link-${i}`}>
              {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
            </button>
          ))}
        </div>
      )}

      <button onClick={() => setOpen(!open)} className="mt-3 text-[11px] opacity-60 hover:opacity-100 flex items-center gap-1" data-testid="insulin-axis-why-toggle">
        <Info className="w-3 h-3" /> Защо двете стават едно число
      </button>
      {open && (
        <div className="text-xs mt-2 leading-relaxed opacity-85" data-testid="insulin-axis-why">
          {data.why_one_number_bg}
          <div className="mt-1 opacity-70">
            Тегла: чувствителност {Math.round(data.weights.sensitivity * 100)}% · храна {Math.round(data.weights.load * 100)}%
            {data.load_effective !== null && data.load_effective !== undefined &&
              ` · натискът от храната влиза с ${data.load_effective}/100 след умножителя`}
          </div>
        </div>
      )}
    </div>
  );
}
